import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ScanLine } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { ScannerModal } from '@/components/scanner';
import { usePermissions } from '@/hooks/use-permissions';
import type { Asset, AssetStatus, AssetTemplate, AssetType, CreateAssetPayload } from '@/lib/types';

const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  active: 'Active',
  available: 'Available',
  deployed: 'Deployed',
  in_use: 'In Use',
  damaged: 'Damaged',
  retired: 'Retired',
  lost: 'Lost',
  quarantined: 'Quarantined',
};

// Mirrors AssetSerializer.allowed_transitions — the choices offered when
// editing an asset's status.
const ALLOWED_STATUS_TRANSITIONS: Partial<Record<AssetStatus, AssetStatus[]>> = {
  active: ['available', 'deployed', 'retired', 'lost', 'damaged', 'in_use', 'quarantined'],
  available: ['deployed', 'retired', 'lost', 'damaged', 'active', 'in_use', 'quarantined'],
  deployed: ['available', 'lost', 'damaged', 'in_use', 'quarantined'],
  in_use: ['available', 'damaged', 'lost', 'quarantined'],
  damaged: ['active', 'available', 'retired', 'lost', 'quarantined'],
  quarantined: ['available', 'retired', 'lost'],
};

// Mirrors AssetSerializer.validate_status: returning quarantined/damaged gear
// to service needs someone who can sign off inspections.
const OUT_OF_SERVICE_STATUSES: AssetStatus[] = ['quarantined', 'damaged'];
const BACK_IN_SERVICE_STATUSES: AssetStatus[] = ['available', 'active'];

function statusOptionsFor(current: AssetStatus, canRecordInspections: boolean): AssetStatus[] {
  const next = ALLOWED_STATUS_TRANSITIONS[current] ?? [];
  const allowed =
    canRecordInspections || !OUT_OF_SERVICE_STATUSES.includes(current)
      ? next
      : next.filter((s) => !BACK_IN_SERVICE_STATUSES.includes(s));
  return [current, ...allowed];
}

const ASSET_TYPE_OPTIONS: { value: AssetType; label: string }[] = [
  { value: 'carabiner', label: 'Carabiner' },
  { value: 'harness', label: 'Harness' },
  { value: 'rope', label: 'Rope' },
  { value: 'lanyard', label: 'Lanyard' },
  { value: 'sling', label: 'Sling' },
  { value: 'descender', label: 'Descender' },
  { value: 'ascender', label: 'Ascender' },
  { value: 'pulley', label: 'Pulley' },
  { value: 'helmet', label: 'Helmet' },
  { value: 'anchor', label: 'Anchor' },
  { value: 'anchor_strap', label: 'Anchor Strap' },
  { value: 'connector', label: 'Connector' },
  { value: 'fall_arrester', label: 'Fall Arrester' },
  { value: 'fall_arrest_accessory', label: 'Fall Arrest Accessory' },
  { value: 'mobile_fall_arrester', label: 'Mobile Fall Arrester' },
  { value: 'rigging_plate', label: 'Rigging Plate' },
  { value: 'rope_clamp', label: 'Rope Clamp' },
  { value: 'swivel', label: 'Swivel' },
  { value: 'other', label: 'Other' },
];

const assetFormSchema = z.object({
  status: z.string().optional(),
  type: z.enum(['carabiner', 'harness', 'rope', 'lanyard', 'sling', 'descender', 'ascender', 'pulley', 'helmet', 'anchor', 'anchor_strap', 'connector', 'fall_arrester', 'fall_arrest_accessory', 'mobile_fall_arrester', 'rigging_plate', 'rope_clamp', 'swivel', 'other'], {
    error: 'Type is required',
  }),
  manufacturer: z.string().min(1, 'Manufacturer is required').max(128, 'Manufacturer must be less than 128 characters'),
  model: z.string().min(1, 'Model is required').max(128, 'Model must be less than 128 characters'),
  serial_number: z
    .string()
    .min(1, 'Serial number is required')
    .max(100, 'Serial number must be less than 100 characters'),
  date_of_manufacture: z.string().optional(),
  length_m: z.string().optional(),
  safe_working_load: z.string().optional(),
  inspection_interval_days: z.string().optional(),
});

type AssetFormValues = z.infer<typeof assetFormSchema>;

interface AssetFormProps {
  asset?: Asset;
  templates?: AssetTemplate[];
  onSubmit: (data: CreateAssetPayload) => void;
  isSubmitting?: boolean;
  onCancel?: () => void;
  initialSerialNumber?: string;
}

export function AssetForm({
  asset,
  templates,
  onSubmit,
  isSubmitting = false,
  onCancel,
  initialSerialNumber,
}: AssetFormProps) {
  const [scannerOpen, setScannerOpen] = useState(false);
  const { canRecordInspections } = usePermissions();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<AssetFormValues>({
    resolver: zodResolver(assetFormSchema),
    defaultValues: {
      status: asset?.status,
      type: asset?.type || 'other',
      manufacturer: asset?.manufacturer || '',
      model: asset?.model || '',
      serial_number: asset?.serial_number || initialSerialNumber || '',
      date_of_manufacture: asset?.date_of_manufacture || '',
      length_m: asset?.length_m || '',
      safe_working_load: asset?.safe_working_load || '',
      inspection_interval_days: asset?.inspection_interval_days?.toString() || '',
    },
  });

  const handleScanResult = (result: string) => {
    setValue('serial_number', result, { shouldValidate: true });
    setScannerOpen(false);
  };

  const handleTemplateSelect = (templateId: string) => {
    if (!templates) return;
    const template = templates.find((t) => t.id === parseInt(templateId, 10));
    if (template) {
      setValue('type', template.type || 'other');
      setValue('manufacturer', template.manufacturer || '');
      setValue('model', template.model || '');
      setValue('date_of_manufacture', template.date_of_manufacture || '');
      setValue('length_m', template.length_m || '');
      setValue('safe_working_load', template.safe_working_load || '');
      setValue('inspection_interval_days', template.inspection_interval_days?.toString() || '');
    }
  };

  const handleFormSubmit = (values: AssetFormValues) => {
    const payload: CreateAssetPayload = {
      type: values.type,
      manufacturer: values.manufacturer,
      model: values.model,
      serial_number: values.serial_number,
    };

    if (asset && values.status && values.status !== asset.status) {
      payload.status = values.status as AssetStatus;
    }

    if (values.date_of_manufacture) payload.date_of_manufacture = values.date_of_manufacture;
    if (values.length_m) payload.length_m = values.length_m;
    if (values.safe_working_load) payload.safe_working_load = values.safe_working_load;
    if (values.inspection_interval_days) {
      const interval = parseInt(values.inspection_interval_days, 10);
      if (!isNaN(interval)) payload.inspection_interval_days = interval;
    }

    onSubmit(payload);
  };

  useEffect(() => {
    if (asset) {
      reset({
        status: asset.status,
        type: asset.type || 'other',
        manufacturer: asset.manufacturer || '',
        model: asset.model || '',
        serial_number: asset.serial_number || '',
        date_of_manufacture: asset.date_of_manufacture || '',
        length_m: asset.length_m || '',
        safe_working_load: asset.safe_working_load || '',
        inspection_interval_days: asset.inspection_interval_days?.toString() || '',
      });
    }
  }, [asset, reset]);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {templates && templates.length > 0 && !asset && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Create from Template</CardTitle>
          </CardHeader>
          <CardContent>
            <Select onValueChange={handleTemplateSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template (optional)" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={String(template.id)}>
                    {template.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500 mt-2">
              Selecting a template will pre-fill the form with default values.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">Type *</Label>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select asset type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.type && (
              <p className="text-sm text-red-500">{errors.type.message}</p>
            )}
            <p className="text-sm text-gray-500">The type of equipment</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="manufacturer">Manufacturer *</Label>
              <Input
                id="manufacturer"
                placeholder="Enter manufacturer"
                {...register('manufacturer')}
                disabled={isSubmitting}
              />
              {errors.manufacturer && (
                <p className="text-sm text-red-500">{errors.manufacturer.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Model *</Label>
              <Input
                id="model"
                placeholder="Enter model"
                {...register('model')}
                disabled={isSubmitting}
              />
              {errors.model && (
                <p className="text-sm text-red-500">{errors.model.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="serial_number">Serial Number *</Label>
              <div className="flex gap-2">
                <Input
                  id="serial_number"
                  placeholder="Enter serial number"
                  {...register('serial_number')}
                  disabled={isSubmitting}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setScannerOpen(true)}
                  disabled={isSubmitting}
                  title="Scan barcode"
                >
                  <ScanLine className="h-4 w-4" />
                </Button>
              </div>
              {errors.serial_number && (
                <p className="text-sm text-red-500">{errors.serial_number.message}</p>
              )}
              <p className="text-sm text-gray-500">Unique identifier for this asset</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_of_manufacture">Date of Manufacture</Label>
              <Input
                id="date_of_manufacture"
                type="date"
                {...register('date_of_manufacture')}
                disabled={isSubmitting}
              />
              {errors.date_of_manufacture && (
                <p className="text-sm text-red-500">{errors.date_of_manufacture.message}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {asset && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="status">Asset Status</Label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptionsFor(asset.status, canRecordInspections).map(
                        (status) => (
                          <SelectItem key={status} value={status}>
                            {ASSET_STATUS_LABELS[status]}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              <p className="text-sm text-gray-500">
                Only valid transitions are listed. Use "Damaged" or
                "Quarantined" to take gear out of service. Returning
                quarantined or damaged gear to service needs a certified
                inspector.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Specifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="length_m">Length (meters)</Label>
              <Input
                id="length_m"
                type="number"
                step="0.01"
                placeholder="Enter length"
                {...register('length_m')}
                disabled={isSubmitting}
              />
              {errors.length_m && (
                <p className="text-sm text-red-500">{errors.length_m.message}</p>
              )}
              <p className="text-sm text-gray-500">For rope assets</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="safe_working_load">Safe Working Load (kg)</Label>
              <Input
                id="safe_working_load"
                type="number"
                step="0.01"
                placeholder="Enter SWL"
                {...register('safe_working_load')}
                disabled={isSubmitting}
              />
              {errors.safe_working_load && (
                <p className="text-sm text-red-500">{errors.safe_working_load.message}</p>
              )}
              <p className="text-sm text-gray-500">Maximum safe load capacity</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="inspection_interval_days">Inspection Interval (days)</Label>
            <Input
              id="inspection_interval_days"
              type="number"
              placeholder="Enter interval"
              {...register('inspection_interval_days')}
              disabled={isSubmitting}
            />
            {errors.inspection_interval_days && (
              <p className="text-sm text-red-500">{errors.inspection_interval_days.message}</p>
            )}
            <p className="text-sm text-gray-500">Days between required inspections</p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : asset ? 'Update Asset' : 'Create Asset'}
        </Button>
      </div>

      <ScannerModal
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={handleScanResult}
        title="Scan Serial Number"
        description="Scan a barcode to fill in the serial number"
      />
    </form>
  );
}
